"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "sonner";

export interface VerificationConfig {
  value: string;
  onChange: (value: string) => void;
  onSend: (value: string) => Promise<boolean>;
  onVerify: (value: string, code: string) => Promise<boolean>;
  resendDelay?: number;
  otpLength?: number;
  disabled?: boolean;
  isVerified?: boolean;
  onVerifiedChange?: (verified: boolean) => void;
}

interface VerificationState {
  value: string;
  otpCode: string;
  isVerified: boolean;
  showOtp: boolean;
  isSending: boolean;
  isVerifying: boolean;
  canResend: boolean;
  timeUntilResend: number;
  error?: string;
}

interface VerificationContextValue {
  state: VerificationState;
  config: VerificationConfig;
  actions: {
    updateValue: (value: string) => void;
    updateOtpCode: (code: string) => void;
    send: () => Promise<void>;
    verify: (code?: string) => Promise<void>;
    cancel: () => void;
    change: () => void;
    clearError: () => void;
    reset: () => void;
  };
}

const VerificationContext = createContext<VerificationContextValue | null>(
  null
);

interface VerificationProviderProps {
  children: ReactNode;
  config: VerificationConfig;
}

export function VerificationProvider({
  children,
  config
}: VerificationProviderProps) {
  const resendTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resendIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resendDelay = config.resendDelay ?? 10000;

  const [state, setState] = useState<VerificationState>({
    canResend: false,
    error: undefined,
    isSending: false,
    isVerified: config.isVerified ?? false,
    isVerifying: false,
    otpCode: "",
    showOtp: false,
    timeUntilResend: 0,
    value: config.value
  });

  // Sync external value changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      isVerified: config.isVerified ?? false,
      value: config.value
    }));
  }, [config.value, config.isVerified]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) {
        clearTimeout(resendTimerRef.current);
      }
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, []);

  const updateValue = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setState(prev => {
        const newState = { ...prev, value: trimmedValue };

        // Reset verification if value changes and was verified
        if (prev.isVerified) {
          newState.isVerified = false;
          newState.showOtp = false;
          newState.otpCode = "";
        }

        return newState;
      });

      config.onChange(trimmedValue);

      // Call onVerifiedChange after state update if needed
      if (state.isVerified) {
        config.onVerifiedChange?.(false);
      }
    },
    [config, state.isVerified]
  );

  const updateOtpCode = useCallback((otpCode: string) => {
    setState(prev => ({ ...prev, otpCode }));
  }, []);

  const handleVerificationError = useCallback((error: Error) => {
    // Extract title from error message if it follows pattern "Title: Description"
    const errorMessage = error.message;
    const colonIndex = errorMessage.indexOf(". ");

    const title =
      colonIndex > -1 && colonIndex < 50
        ? errorMessage.substring(0, colonIndex)
        : "Error";

    const description =
      colonIndex > -1 && colonIndex < 50
        ? errorMessage.substring(colonIndex + 2)
        : errorMessage;

    toast.error(title, { description });
  }, []);

  const send = useCallback(async () => {
    if (!state.value || state.isSending || config.disabled) {
      return;
    }

    setState(prev => ({ ...prev, error: undefined, isSending: true }));

    try {
      const success = await config.onSend(state.value);
      if (!success) {
        setState(prev => ({
          ...prev,
          isSending: false
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        canResend: false,
        isSending: false,
        showOtp: true,
        timeUntilResend: Math.floor(resendDelay / 1000)
      }));

      // Set up resend timer
      if (resendTimerRef.current) {
        clearTimeout(resendTimerRef.current);
      }
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }

      // Update countdown every second
      resendIntervalRef.current = setInterval(() => {
        setState(prev => {
          const newTime = Math.max(0, prev.timeUntilResend - 1);
          if (newTime === 0 && resendIntervalRef.current) {
            clearInterval(resendIntervalRef.current);
          }
          return { ...prev, timeUntilResend: newTime };
        });
      }, 1000);

      resendTimerRef.current = setTimeout(() => {
        setState(prev => ({ ...prev, canResend: true }));
      }, resendDelay);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to send verification code";
      setState(prev => ({
        ...prev,
        error: errorMessage,
        isSending: false
      }));
      handleVerificationError(
        error instanceof Error ? error : new Error(errorMessage)
      );
    }
  }, [
    state.value,
    state.isSending,
    config.disabled,
    config,
    resendDelay,
    handleVerificationError
  ]);

  const verify = useCallback(
    async (code?: string) => {
      const otpToVerify = code ?? state.otpCode;
      const expectedLength = config.otpLength ?? 6;

      if (!otpToVerify || otpToVerify.length !== expectedLength) {
        return;
      }

      setState(prev => ({ ...prev, error: undefined, isVerifying: true }));

      try {
        const success = await config.onVerify(state.value, otpToVerify);

        if (success) {
          setState(prev => ({
            ...prev,
            canResend: false,
            isVerified: true,
            isVerifying: false,
            otpCode: "",
            showOtp: false
          }));

          // Clear timers
          if (resendTimerRef.current) {
            clearTimeout(resendTimerRef.current);
          }
          if (resendIntervalRef.current) {
            clearInterval(resendIntervalRef.current);
          }

          config.onVerifiedChange?.(true);
        } else {
          setState(prev => ({
            ...prev,
            error: "Verification failed",
            isVerifying: false,
            otpCode: ""
          }));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Verification failed";
        setState(prev => ({
          ...prev,
          error: errorMessage,
          isVerifying: false,
          otpCode: ""
        }));
        handleVerificationError(
          error instanceof Error ? error : new Error(errorMessage)
        );
      }
    },
    [state.otpCode, state.value, config, handleVerificationError]
  );

  const cancel = useCallback(() => {
    setState(prev => ({
      ...prev,
      canResend: false,
      error: undefined,
      otpCode: "",
      showOtp: false,
      timeUntilResend: 0
    }));

    // Clear timers
    if (resendTimerRef.current) {
      clearTimeout(resendTimerRef.current);
    }
    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }
  }, []);

  const change = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: undefined,
      isVerified: false,
      otpCode: "",
      showOtp: false
    }));
    config.onVerifiedChange?.(false);
  }, [config]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: undefined }));
  }, []);

  const reset = useCallback(() => {
    setState({
      canResend: false,
      error: undefined,
      isSending: false,
      isVerified: false,
      isVerifying: false,
      otpCode: "",
      showOtp: false,
      timeUntilResend: 0,
      value: ""
    });

    // Clear timers
    if (resendTimerRef.current) {
      clearTimeout(resendTimerRef.current);
    }
    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }

    config.onChange("");
    config.onVerifiedChange?.(false);
  }, [config]);

  const contextValue = useMemo<VerificationContextValue>(
    () => ({
      actions: {
        cancel,
        change,
        clearError,
        reset,
        send,
        updateOtpCode,
        updateValue,
        verify
      },
      config,
      state
    }),
    [
      state,
      config,
      updateValue,
      updateOtpCode,
      send,
      verify,
      cancel,
      change,
      clearError,
      reset
    ]
  );

  return (
    <VerificationContext.Provider value={contextValue}>
      {children}
    </VerificationContext.Provider>
  );
}

export function useVerification() {
  const context = useContext(VerificationContext);
  if (!context) {
    throw new Error(
      "useVerification must be used within a VerificationProvider"
    );
  }
  return context;
}
